import { CarAxleType, TireLoadIndex, TireSpeedIndex } from '@navio/nsdk';
import { type FilterControl, filterUrlState } from 'noddi-ui-web';
import { TIRE_LOAD_INDEX_OPTIONS, TIRE_SPEED_INDEX_OPTIONS } from 'noddi-util';
import { createEnumParam, withDefault } from 'use-query-params';
import {
  hasTireFitmentConstraints,
  type TireAxleFitment,
  tireAxleFitmentKey,
  tireAxleFitmentsParam,
  tireAxleFitmentsQuery
} from './tireAxleFitments';
import { tireInventoryFilterUnit } from './tireInventoryFilterUnit';

/** URL keys of the axle requirements; `useTireFitmentFilterParams` writes them too. */
export const TIRE_AXLE_URL_KEYS = { fitments: filterUrlState.urlKey('axleFitments'), axle: 'tire-axle' } as const;

const AXLES = ['any', CarAxleType.FRONT, CarAxleType.REAR] as const;
type AxleScope = (typeof AXLES)[number];

export const tireAxleUrlParams = {
  [TIRE_AXLE_URL_KEYS.fitments]: withDefault(tireAxleFitmentsParam, []),
  [TIRE_AXLE_URL_KEYS.axle]: createEnumParam([...AXLES])
};

const AXLE_OPTIONS: { value: AxleScope; label: string }[] = [
  { value: 'any', label: 'Any axle' },
  { value: CarAxleType.FRONT, label: 'Front axle' },
  { value: CarAxleType.REAR, label: 'Rear axle' }
];

const LOAD_INDEX_OPTIONS = TIRE_LOAD_INDEX_OPTIONS.map((option) => ({ ...option, value: Number(option.value) }));
const toLoadIndex = (value: string | number | null) =>
  Object.values(TireLoadIndex).find((index): index is TireLoadIndex => typeof index === 'number' && index === value);
const toSpeedIndex = (value: string | number | null) => Object.values(TireSpeedIndex).find((index) => index === value);
const toAxle = (value: string | number | null) => AXLES.find((axle) => axle === value);

/**
 * Size + minimum indexes per axle, kept paired in one JSON param: a front size never matches a rear minimum. The
 * axle scope narrows which requirements are sent. Several controls on one param, so a `compose`.
 */
export const tireAxleFitmentsFilter = tireInventoryFilterUnit.compose(
  'tireAxleFitments',
  tireAxleUrlParams,
  (values, setValues) => {
    const fitments = values[TIRE_AXLE_URL_KEYS.fitments] as TireAxleFitment[];
    const axle = values[TIRE_AXLE_URL_KEYS.axle] as AxleScope | null | undefined;
    const setFitments = (next: (previous: TireAxleFitment[]) => TireAxleFitment[]) =>
      setValues((latest) => ({
        [TIRE_AXLE_URL_KEYS.fitments]: next(latest[TIRE_AXLE_URL_KEYS.fitments] as TireAxleFitment[])
      }));
    const setAxle = (next: AxleScope | undefined) => setValues(() => ({ [TIRE_AXLE_URL_KEYS.axle]: next }));
    const updateMinimum = <Key extends 'loadIndexGte' | 'speedIndexGte'>(
      type: CarAxleType,
      key: Key,
      value: TireAxleFitment[Key] | undefined
    ) =>
      setFitments((previous) => {
        const entries = previous.some((entry) => entry.axle === type) ? previous : [...previous, { axle: type }];
        return entries
          .map((entry) => (entry.axle === type ? { ...entry, [key]: value } : entry))
          .filter(hasTireFitmentConstraints);
      });

    const controls: FilterControl[] = [
      {
        id: 'tireAxle',
        type: 'single-select',
        title: 'Axle',
        description: 'Match requirements for the front, rear, or any axle.',
        options: AXLE_OPTIONS,
        canExclude: false,
        value: { include: axle ?? null, exclude: [] },
        onChange: (next) => setAxle(toAxle(next.include)),
        reset: () => setAxle(undefined),
        isInitial: values[TIRE_AXLE_URL_KEYS.axle] === undefined
      },
      {
        id: 'axleFitments',
        type: 'multi-select',
        title: 'Axle requirements',
        description: 'Each size stays paired with its minimum indexes. Match any selected requirement.',
        options: fitments.map((fitment) => ({ value: tireAxleFitmentKey(fitment), label: fitmentLabel(fitment) })),
        canExclude: false,
        value: { include: fitments.map(tireAxleFitmentKey), exclude: [] },
        onChange: (next) =>
          setFitments((previous) => previous.filter((fitment) => next.include.includes(tireAxleFitmentKey(fitment)))),
        reset: () => setFitments(() => []),
        isInitial: fitments.length === 0
      },
      ...[CarAxleType.FRONT, CarAxleType.REAR].flatMap((type) => axleMinimumControls(type, fitments, updateMinimum))
    ];

    return { controls, params: { axleFitments: tireAxleFitmentsQuery(fitments, axle) } };
  }
);

const axleMinimumControls = (
  type: CarAxleType,
  fitments: TireAxleFitment[],
  updateMinimum: <Key extends 'loadIndexGte' | 'speedIndexGte'>(
    type: CarAxleType,
    key: Key,
    value: TireAxleFitment[Key] | undefined
  ) => void
): FilterControl[] => {
  const entries = fitments.filter((entry) => entry.axle === type);
  const load = uniqueDefined(entries.map((entry) => entry.loadIndexGte));
  const speed = uniqueDefined(entries.map((entry) => entry.speedIndexGte));
  const label = type === CarAxleType.FRONT ? 'Front axle' : 'Rear axle';
  return [
    {
      id: `${type}LoadIndex`,
      type: 'single-select',
      title: `${label} minimum load index`,
      description: 'Minimum load index for this axle.',
      options: LOAD_INDEX_OPTIONS,
      canExclude: false,
      // Several requirements with different minimums show none: no one value is the selection.
      value: { include: load.length === 1 ? (load[0] ?? null) : null, exclude: [] },
      onChange: (next) => updateMinimum(type, 'loadIndexGte', toLoadIndex(next.include)),
      reset: () => updateMinimum(type, 'loadIndexGte', undefined),
      isInitial: load.length === 0
    },
    {
      id: `${type}SpeedIndex`,
      type: 'single-select',
      title: `${label} minimum speed index`,
      description: 'Minimum speed index for this axle.',
      options: TIRE_SPEED_INDEX_OPTIONS,
      canExclude: false,
      value: { include: speed.length === 1 ? (speed[0] ?? null) : null, exclude: [] },
      onChange: (next) => updateMinimum(type, 'speedIndexGte', toSpeedIndex(next.include)),
      reset: () => updateMinimum(type, 'speedIndexGte', undefined),
      isInitial: speed.length === 0
    }
  ];
};

const fitmentLabel = (fitment: TireAxleFitment) =>
  [
    fitment.axle === CarAxleType.FRONT ? 'Front' : 'Rear',
    fitment.dimension,
    fitment.loadIndexGte !== undefined ? `load ${fitment.loadIndexGte}+` : undefined,
    fitment.speedIndexGte !== undefined ? `speed ${fitment.speedIndexGte}+` : undefined
  ]
    .filter(Boolean)
    .join(' · ');

const uniqueDefined = <T>(values: (T | undefined)[]): T[] => [
  ...new Set(values.filter((value): value is T => value !== undefined))
];
